/**
 * Admin overview. Spec 11.4: "Active sessions, connections, 24h AI spend, error rate, DB
 * size, job queue depth."
 */
import { createFileRoute } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '../../../components/ui/Alert';
import { Card } from '../../../components/ui/Card';
import { SkeletonList } from '../../../components/ui/Skeleton';
import { useAdminOverview } from '../../../features/admin/api';
import { errorKeyFor } from '../../../lib/errors';

export const Route = createFileRoute('/$lang/admin/')({ component: Page });

function Page(): ReactNode {
  const { t, i18n } = useTranslation(['admin', 'common']);
  const overview = useAdminOverview();

  if (overview.error !== null) {
    return <Alert tone="error">{t(errorKeyFor(overview.error))}</Alert>;
  }

  if (overview.isLoading || overview.data === undefined) {
    return <SkeletonList rows={4} label={t('admin:nav.overview')} />;
  }

  const data = overview.data;
  const number = new Intl.NumberFormat(i18n.language);

  return (
    <div className="flex flex-col gap-6">
      <h1 className="text-xl font-semibold text-ink">{t('admin:nav.overview')}</h1>

      <dl className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <Figure
          label={t('admin:overview.activeSessions')}
          value={number.format(data.activeSessions)}
        />
        <Figure
          label={t('admin:overview.connections')}
          value={number.format(data.liveConnections)}
        />
        <Figure
          label={t('admin:overview.aiSpend')}
          /* Micro-USD in the database; dollars are what an operator thinks in. */
          value={`$${(data.aiSpendMicros24h / 1_000_000).toFixed(2)}`}
        />
        <Figure
          label={t('admin:overview.dbSize')}
          value={`${(data.databaseSizeBytes / 1_048_576).toFixed(1)} MB`}
        />
        <Figure label={t('admin:overview.users')} value={number.format(data.usersTotal)} />
        <Figure label={t('admin:overview.suspended')} value={number.format(data.usersSuspended)} />
      </dl>

      <Card title={t('admin:overview.jobQueue')}>
        {/*
         * Null, not zero. There is no job queue — pg-boss is in the stack table and has
         * never been wired up — and "0" would claim an empty queue rather than an absent one.
         */}
        <p className="text-sm text-ink-muted">
          {data.jobQueueDepth === null
            ? t('admin:overview.jobQueueNone')
            : number.format(data.jobQueueDepth)}
        </p>
      </Card>
    </div>
  );
}

function Figure({ label, value }: { readonly label: string; readonly value: string }): ReactNode {
  return (
    <div className="rounded-lg border border-border bg-surface-raised px-4 py-3">
      <dt className="text-xs text-ink-muted">{label}</dt>
      <dd className="mt-1 text-2xl font-semibold tabular-nums text-ink">{value}</dd>
    </div>
  );
}
